<template>
  <CCard v-if="campaignDetails && campaignDetails.id">
    <CCardHeader>
      Buyer Campaign :
      {{ campaignDetails.name }}
      <a
        href
        @click.prevent="copyIdToClipboard(campaignDetails.id)"
      ><CIcon
        name="cil-copy"
      /></a>
      <CBadge
        v-for="(badge, index) in getStatusBadges(campaignDetails)"
        :key="`status-${index}`"
        :class="index === 0 ? 'ml-5' : 'ml-2'"
        :color="badge.color"
      >
        {{ badge.label }}
      </CBadge>
      <CBadge
        v-if="isCpaCategory"
        class="ml-2"
        color="info"
      >
        CPA
      </CBadge>
      <span class="schedule-separator" />
      <ScheduleBadges :hours-of-operation="campaignDetails.routing && campaignDetails.routing.schedule && campaignDetails.routing.schedule.hoursOfOperation" />
      <template v-if="spendBudget">
        <span class="schedule-separator" />
        <span
          :class="['spend-budget-inline', spendBudget.spendBudgetZero ? 'spend-budget-inline--zero' : 'spend-budget-inline--active']"
          @mouseenter="!spendBudget.spendBudgetZero && (showSpendPopup = true)"
          @mouseleave="showSpendPopup = false"
        >
          Avg Weekly Spend: {{ spendBudget.spendBudgetPercent }}%
          <SpendBudgetPopup
            v-if="showSpendPopup"
            :weekly-spend-data="spendBudget.weeklySpendData"
            :avg-percent="spendBudget.spendBudgetPercent"
            light
          />
        </span>
      </template>
      <span class="float-right">
        <CButton
          v-if="!canCampaignBeResumed(campaignDetails) && !isDeleted"
          color="warning"
          size="sm"
          @click="openPauseResumeModal(true)"
        >
          Pause
        </CButton>
        <CButton
          v-if="canCampaignBeResumed(campaignDetails) && !isDeleted"
          color="success"
          size="sm"
          @click="openPauseResumeModal(false)"
        >
          Resume
        </CButton>
      </span>
    </CCardHeader>

    <CModal
      :title="isPauseAction ? 'Pause Campaign' : 'Resume Campaign'"
      :color="isPauseAction ? 'warning' : 'success'"
      :show.sync="showPauseResumeModal"
    >
      Are you sure you want to {{ isPauseAction ? 'pause' : 'resume' }} <strong><i>{{ campaignDetails.name }}</i></strong> buyer campaign?

      <CTextarea
        v-model="pauseResumeNote"
        class="mt-2"
        name="Note"
        label="Note"
        :is-valid="isPauseResumeNoteValid"
        invalid-feedback="Note is mandatory"
        small
        required
        rows="3"
        :debounce="500"
      />

      <div
        v-if="isPauseAction"
        class="mt-3"
      >
        <CInputCheckbox
          v-model="isStrictPause"
          :custom="false"
        >
          <template #label>
            <strong>Strict Pause</strong> - Prevents buyers from resuming this campaign
          </template>
        </CInputCheckbox>
      </div>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isPauseResumeLoading"
          :disabled="!isPauseResumeNoteValid"
          @click="onConfirmPauseOrResume"
        >
          {{ isPauseAction ? 'Pause' : 'Resume' }}
        </ButtonLoading>

        <CButton
          color="danger"
          @click="showPauseResumeModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
    <CCardBody>
      <CForm
        novalidate
        @submit.prevent
      >
        <CInput
          v-model="$v.campaignDetailsLocal.name.$model"
          type="text"
          label="Name"
          add-label-classes="text-sm-right pr-4"
          name="Name"
          placeholder="Campaign Name"
          :is-valid="checkIfValid(['campaignDetailsLocal', 'name'], submitted)"
          :horizontal="{ label: 'col-sm-3', input: 'col-sm-6'}"
          invalid-feedback="Campaign name cannot be left blank"
        />

        <CSelect
          id="buyer-campaign-category-dropdown"
          v-model="$v.campaignDetailsLocal.categoryId.$model"
          :value.sync="campaignDetailsLocal.categoryId"
          add-label-classes="text-sm-right pr-4"
          data-cy="buyer-campaign-category"
          label="Category"
          :is-valid="checkIfValid(['campaignDetailsLocal', 'categoryId'], submitted)"
          :horizontal="{ label: 'col-sm-3', input: 'col-sm-6'}"
          :options="categoriesFormatted"
          placeholder="Please select"
          invalid-feedback="Please select a category"
          @change="onCategoryChange"
        />

        <CSelect
          id="buyer-campaign-language-dropdown"
          v-model="$v.campaignDetailsLocal.languageId.$model"
          :value.sync="campaignDetailsLocal.languageId"
          add-label-classes="text-sm-right pr-4"
          data-cy="buyer-campaign-language"
          label="Language"
          :is-valid="checkIfValid(['campaignDetailsLocal', 'languageId'], submitted)"
          :horizontal="{ label: 'col-sm-3', input: 'col-sm-6'}"
          :options="languageOptions"
          placeholder="Please select"
          invalid-feedback="Please select a language"
        />

        <CSelect
          id="buyer-campaign-country-dropdown"
          v-model="$v.campaignDetailsLocal.countryId.$model"
          :value.sync="campaignDetailsLocal.countryId"
          add-label-classes="text-sm-right pr-4"
          data-cy="buyer-campaign-country-dropdown"
          label="Country"
          :is-valid="checkIfValid(['campaignDetailsLocal', 'countryId'], submitted)"
          :horizontal="{ label: 'col-sm-3', input: 'col-sm-6'}"
          :options="countriesFormatted"
          placeholder="Please select"
          invalid-feedback="Please select a country"
        />

        <CInput
          v-model="lastReviewer.email"
          label="Reviewed By"
          horizontal
          plaintext
          readonly     
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"               
        />

        <CInput
          v-model="lastReviewer.date"
          label="Reviewed At"
          horizontal
          plaintext
          readonly     
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"   
        />

        <CInput
          v-model="lastReviewer.decisionRemarks"
          label="Review Notes"
          horizontal
          plaintext
          readonly
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"
        />

        <CInput
          v-model="lastPausedResumedDate"
          label="Last Pause/Resume"
          horizontal
          plaintext
          readonly
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"
        />

        <CInput
          v-model="lastPausedResumedBy"
          label="Paused/Resumed By"
          horizontal
          plaintext
          readonly
          add-label-classes="text-right pr-4"
          add-input-classes="w-50"
        />
      </CForm>

      <div class="row">
        <div class="col-sm-3 pr-4" />
        <div class="col-sm-9">
          <ButtonLoading
            color="primary"
            :loading="isLoading"
            :disabled="!isFormEdited"
            horizontal
            size="sm"
            label=""
            @click="updateDetails"
          >
            Update
          </ButtonLoading>
        </div>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import { required } from 'vuelidate/lib/validators'
import { showSuccessMessage } from '@/notification-utils'
import formMixins from '@/mixins/form-mixins'
import formGenericMixin from '@/mixins/form-generic-mixin'
import { dateTimeFormat, sortByLabel } from '@/utils'
import buyerCampaignStatusMixin from '@/views/Campaigns/buyer/mixins/buyer-campaign-status-mixin'
import ScheduleBadges from '@/views/Campaigns/buyer/components/ScheduleBadges'
import SpendBudgetPopup from '@/components/SpendBudgetPopup.vue'
import { computeSpendBudget } from '@/store/modules/insights.store'

export default {
  name: 'MainSection',

  components: { ScheduleBadges, SpendBudgetPopup },

  mixins: [copyToClipboardMixin, formMixins, formGenericMixin, buyerCampaignStatusMixin],

  props: {
    campaignDetails: {
      type: Object,
      default: () => {}
    },
    isCpaCategory: {
      type: Boolean,
      default: false
    }
  },
  validations() {
    return {
      campaignDetailsLocal: {
        name: {
          required
        },

        categoryId: {
          required
        },

        languageId: {
          required
        },

        countryId: {
          required
        }
      }
    }
  },
  data() {
    return {
      isLoading: false,
      campaignDetailsLocal: {
        name: '',
        categoryId: null,
        countryId: null
      },
      showPauseResumeModal: false,
      isPauseAction: true,
      pauseResumeNote: '',
      isStrictPause: false,
      isPauseResumeLoading: false,
      showSpendPopup: false
    }
  },

  computed: {
    ...mapState('country', ['countries']),
    ...mapState('category', ['categories']),
    ...mapState('insights', ['campaignSpend']),

    spendBudget() {
      const spendEntry = this.campaignSpend.find(s => s.campaignId === this.campaignDetails?.id)
      if (!spendEntry) {
        return null
      }

      return computeSpendBudget(spendEntry.weeklySpend, this.campaignDetails?.budget?.dailyBudgetInUSD)
    },

    lastReviewer(){
      const lastReviewDetails = this.campaignDetailsLocal?.lastReview
      if (!lastReviewDetails){
        return {
          email: "N/A"
        }
      }

      return {
        email: lastReviewDetails.decidedByUserEmail,
        date: this.formatDate(lastReviewDetails.decisionDT),
        remarks: lastReviewDetails.decisionRemarks
      }
    },

    countriesFormatted() {
      return this.countries.map(country => {
        return {
          value: country.id,
          label: country.name
        }
      }).sort(sortByLabel)
    },
    
    categoriesFormatted() {
      return this.categories.map(category => {
        return {
          value: category.id,
          label: category.name
        }
      }).sort(sortByLabel)
    },

    languageOptions() {
      return this.categories?.find(c => c.id === this.campaignDetailsLocal.categoryId)?.languages.map(l => ({value: l.id, label: l.name})).sort(sortByLabel) ?? []
    },

    isDeleted() {
      return this.campaignDetails?.status?.toLowerCase() === 'deleted'
    },

    isPauseResumeNoteValid() {
      return !!this.pauseResumeNote.trim().length
    },

    lastPausedResumedDate() {
      const date = this.campaignDetailsLocal?.lastPausedResumedDate
      return date ? this.formatDate(date) : 'N/A'
    },

    lastPausedResumedBy() {
      const by = this.campaignDetailsLocal?.lastPausedResumedBy
      return by?.name ?? by?.id ?? 'N/A'
    }
  },
  watch: {
    campaignDetails: {
      immediate: true,
      handler(newValue) {
        this.campaignDetailsLocal = {
          ...newValue,
          categoryId: newValue.category.id,
          countryId: newValue.country.id,
          languageId:  newValue.language.id,
          lastReview: newValue.lastReview
        }
      }
    }
  },

  async created() {
    if (this.countries.length === 0) {
      await this.list()
    }

    if (this.categories.length === 0) {
      await this.listCategories()
    }
  },

  methods: {
    ...mapActions('country', ['list']),
    ...mapActions('category', { listCategories: 'list' }),
    ...mapActions('campaign', ['updateCampaignDetails', 'pauseBuyerCampaign', 'resumeBuyerCampaign']),

    async updateDetails() {
      this.submitted = true

      if (this.$v.$invalid) {
        return
      }

      const campaignId = this.campaignDetailsLocal.id
      const updateObject = {
        name: this.campaignDetailsLocal.name,
        category: this.campaignDetailsLocal.categoryId,
        country: this.campaignDetailsLocal.countryId,
        language: this.campaignDetailsLocal.languageId,
      }
      try {
        this.isLoading = true
        await this.updateCampaignDetails({id: campaignId, updateObject})
        showSuccessMessage('Updated campaign details')
      } catch(error) {
        console.error(error.message)
      } finally {
        this.isLoading = false
      }
    },

    copyIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Campaign ID copied to clipboard')
    },

    onCategoryChange() {
      this.campaignDetailsLocal.languageId = null
    },

    formatDate(date) {
      return dateTimeFormat.format(new Date(date))
    },

    openPauseResumeModal(isPause) {
      this.pauseResumeNote = ''
      this.isStrictPause = false
      this.isPauseAction = isPause
      this.showPauseResumeModal = true
    },

    async onConfirmPauseOrResume() {
      try {
        this.isPauseResumeLoading = true

        if (this.isPauseAction) {
          await this.pauseBuyerCampaign({
            id: this.campaignDetails.id,
            remarks: this.pauseResumeNote,
            isStrict: this.isStrictPause
          })

          showSuccessMessage(this.isStrictPause ? 'Strictly paused buyer campaign' : 'Paused buyer campaign')
        } else {
          await this.resumeBuyerCampaign({
            id: this.campaignDetails.id,
            remarks: this.pauseResumeNote,
            status: this.campaignDetails.status
          })

          showSuccessMessage('Resumed buyer campaign')
        }

        this.showPauseResumeModal = false
        this.pauseResumeNote = ''
        this.$emit('pause-resume-completed')
      } catch {
        //
      } finally {
        this.isPauseResumeLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule-separator {
  display: inline-block;
  width: 1px;
  height: 16px;
  background-color: rgba(0, 0, 0, 0.15);
  vertical-align: middle;
  margin: 0 8px;
}
</style>
